import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { API, authHeaders } from "@/lib/adminApi";
import { ADMIN_RECORDABLE_PROVIDERS, PROVIDER_LABELS } from "@/lib/adminUtils";
import { FormSection, PanelField, labelCls, selectCls } from "./NewMemberPanel";
import CheckoutLinkResult from "./CheckoutLinkResult";
import StatusBadge from "./StatusBadge";
import Spinner from "./Spinner";

const todayIso = () => new Date().toISOString().slice(0, 10);

const EMPTY_CHILD = { first_name: "", last_name: "", birth_date: "" };
const EMPTY_PAYMENT_FORM = { amount: "", provider: "especes", paid_at: todayIso(), reference: "", note: "" };
const EMPTY_CLAIM_FORM = { target: "member", date_of_death: "", estimated_amount: "", note: "" };
const EMPTY_CLAIM_PAYMENT_FORM = { paid_amount: "", payment_method: "especes", paid_at: todayIso(), reference: "", note: "" };

const childKey = (firstName, lastName) => `${firstName || ""}|${lastName || ""}`;

/* ── Fiche membre : consultation, édition, historique et enregistrement de paiements ── */
const MemberPanel = ({ memberId, onClose, onChanged }) => {
  const { year, association } = useOutletContext();

  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);
  const [payments, setPayments] = useState([]);
  const [claims, setClaims] = useState([]);
  const [balance, setBalance] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ ...EMPTY_PAYMENT_FORM, year });
  const [recordingPayment, setRecordingPayment] = useState(false);

  const [paymentLink, setPaymentLink] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);

  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimForm, setClaimForm] = useState(EMPTY_CLAIM_FORM);
  const [declaringClaim, setDeclaringClaim] = useState(false);

  // Sinistre en cours de paiement (id) plutôt qu'un simple booléen : un membre
  // peut avoir plusieurs sinistres en attente en même temps (le sien + un par
  // enfant décédé), chacun avec son propre formulaire de paiement.
  const [payingClaimId, setPayingClaimId] = useState(null);
  const [claimPaymentForm, setClaimPaymentForm] = useState(EMPTY_CLAIM_PAYMENT_FORM);
  const [payingClaim, setPayingClaim] = useState(false);
  const [cancellingClaimId, setCancellingClaimId] = useState(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/members/${memberId}?year=${year}`, { headers: authHeaders() });
      setMember(res.data.member);
      setPayments(res.data.payments || []);
      setClaims(res.data.claims || []);
      setBalance(res.data.balance || null);
      setForm({ ...res.data.member });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du chargement du membre.");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [memberId, year, onClose]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const hasChildren = form?.has_children_under_18 ?? ((form?.children || []).length > 0);

  const setHasChildren = (value) => {
    setForm((f) => ({
      ...f,
      has_children_under_18: value,
      children: value ? (f.children?.length ? f.children : [{ ...EMPTY_CHILD }]) : [],
    }));
  };

  const addChild = () => {
    setForm((f) => ({ ...f, children: [...(f.children || []), { ...EMPTY_CHILD }] }));
  };

  const removeChild = (index) => {
    setForm((f) => ({ ...f, children: f.children.filter((_, i) => i !== index) }));
  };

  const updateChild = (index, key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({
      ...f,
      children: f.children.map((c, i) => (i === index ? { ...c, [key]: value } : c)),
    }));
  };

  const setPayment = (key) => (e) => {
    const value = e.target.value;
    setPaymentForm((f) => ({ ...f, [key]: value }));
  };

  const setClaimField = (key) => (e) => {
    const value = e.target.value;
    setClaimForm((f) => ({ ...f, [key]: value }));
  };

  const setClaimPaymentField = (key) => (e) => {
    const value = e.target.value;
    setClaimPaymentForm((f) => ({ ...f, [key]: value }));
  };

  const isDeceased = member?.status === "deceased";
  const isSettled = !!balance?.is_settled;
  const isPartial = !isSettled && (balance?.paid_amount ?? 0) > 0;

  const activeClaims = claims.filter((c) => c.status !== "cancelled");
  const hasMemberClaim = activeClaims.some((c) => c.beneficiary_type === "member");
  const claimedChildKeys = new Set(
    activeClaims
      .filter((c) => c.beneficiary_type === "child")
      .map((c) => childKey(c.child_first_name, c.child_last_name))
  );
  const availableChildren = (member?.children || []).filter(
    (c) => !claimedChildKeys.has(childKey(c.first_name, c.last_name))
  );
  const canDeclareClaim = !hasMemberClaim || availableChildren.length > 0;

  const claimTargetLabel = (c) =>
    c.beneficiary_type === "child" ? `${c.child_first_name} ${c.child_last_name} (enfant)` : "Le membre";

  const memberName = member ? `${member.first_name || ""} ${member.last_name || ""}`.trim() : "";

  // Lien de paiement en cours : PaymentRecord pending Stripe porteur d'un jeton.
  const linkPayment = payments.find(
    (p) => p.provider === "stripe" && p.status === "pending" && p.metadata?.link_token
  );

  const generatePaymentLink = async ({ regenerate = false } = {}) => {
    setLinkBusy(true);
    try {
      const res = await axios.post(
        `${API}/payments/stripe/generate-checkout-link`,
        { member_id: memberId, regenerate },
        { headers: authHeaders() }
      );
      setPaymentLink({ url: res.data.link_url, amount: res.data.amount, expires_at: res.data.expires_at });
      if (regenerate) toast.success("Nouveau lien généré — l'ancien n'est plus valable.");
      await fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération du lien.");
    } finally {
      setLinkBusy(false);
    }
  };

  const linkUrl = paymentLink?.url
    || (linkPayment && `${window.location.origin}/paiement/${linkPayment.metadata.link_token}`);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const children = (form.children || [])
        .filter((c) => c.first_name.trim() || c.last_name.trim())
        .map((c) => ({ ...c, birth_date: c.birth_date || null }));
      await axios.patch(
        `${API}/members/${memberId}`,
        { ...form, children, has_children_under_18: children.length > 0 },
        { headers: authHeaders() }
      );
      toast.success("Membre mis à jour.");
      onChanged();
      await fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) { toast.error("Le montant doit être positif."); return; }
    setRecordingPayment(true);
    try {
      await axios.post(
        `${API}/payments/admin-record`,
        {
          member_id: memberId,
          amount,
          provider: paymentForm.provider,
          year: parseInt(paymentForm.year, 10),
          paid_at: paymentForm.paid_at || undefined,
          reference: paymentForm.reference || undefined,
          note: paymentForm.note || undefined,
        },
        { headers: authHeaders() }
      );
      toast.success("Paiement enregistré.");
      setShowPaymentForm(false);
      setPaymentForm({ ...EMPTY_PAYMENT_FORM, year });
      onChanged();
      await fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement du paiement.");
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleDeclareClaim = async (e) => {
    e.preventDefault();
    const isChildTarget = claimForm.target.startsWith("child:");
    const selectedChild = isChildTarget
      ? availableChildren[parseInt(claimForm.target.split(":")[1], 10)]
      : null;
    if (isChildTarget && !selectedChild) { toast.error("Sélectionnez un enfant."); return; }

    setDeclaringClaim(true);
    try {
      await axios.post(
        `${API}/claims/admin-declare`,
        {
          member_id: memberId,
          beneficiary_type: isChildTarget ? "child" : "member",
          child_first_name: selectedChild?.first_name,
          child_last_name: selectedChild?.last_name,
          child_birth_date: selectedChild?.birth_date,
          date_of_death: claimForm.date_of_death || undefined,
          estimated_amount: claimForm.estimated_amount ? parseFloat(claimForm.estimated_amount) : undefined,
          note: claimForm.note || undefined,
        },
        { headers: authHeaders() }
      );
      toast.success("Sinistre déclaré.");
      setShowClaimForm(false);
      setClaimForm(EMPTY_CLAIM_FORM);
      onChanged();
      await fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la déclaration du sinistre.");
    } finally {
      setDeclaringClaim(false);
    }
  };

  const handlePayClaim = async (e) => {
    e.preventDefault();
    const paidAmount = parseFloat(claimPaymentForm.paid_amount);
    if (!paidAmount || paidAmount <= 0) { toast.error("Le montant doit être positif."); return; }
    setPayingClaim(true);
    try {
      await axios.patch(
        `${API}/claims/${payingClaimId}/pay`,
        {
          paid_amount: paidAmount,
          payment_method: claimPaymentForm.payment_method,
          paid_at: claimPaymentForm.paid_at || undefined,
          reference: claimPaymentForm.reference || undefined,
          note: claimPaymentForm.note || undefined,
        },
        { headers: authHeaders() }
      );
      toast.success("Paiement du sinistre enregistré.");
      setPayingClaimId(null);
      setClaimPaymentForm(EMPTY_CLAIM_PAYMENT_FORM);
      onChanged();
      await fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement du paiement.");
    } finally {
      setPayingClaim(false);
    }
  };

  const handleCancelClaim = async (claimId) => {
    if (!window.confirm("Annuler cette déclaration de sinistre ?")) return;
    setCancellingClaimId(claimId);
    try {
      await axios.post(`${API}/claims/${claimId}/cancel`, {}, { headers: authHeaders() });
      toast.success("Déclaration annulée.");
      onChanged();
      await fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'annulation.");
    } finally {
      setCancellingClaimId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col overflow-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100" style={{ backgroundColor: "#111827" }}>
          <div>
            <h2 className="text-xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, color: "#F9F6F0" }}>
              {member ? `${member.first_name || ""} ${member.last_name || ""}`.trim() || "Membre" : "Membre"}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs" style={{ color: "#6B7280" }}>Fiche membre · exercice {year}</p>
              {!loading && (
                <StatusBadge color={isDeceased ? "slate" : isSettled ? "emerald" : isPartial ? "amber" : "amber"}>
                  {isDeceased ? "Décédé" : isSettled ? "À jour" : isPartial ? "Partielle" : "En attente"}
                </StatusBadge>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg transition-colors hover:bg-white/10">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: "#6B7280" }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading || !form ? (
          <div className="flex-1 flex items-center justify-center"><Spinner /></div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            <form id="member-edit-form" onSubmit={handleSave} className="space-y-6">
              <FormSection title="Identité">
                <div className="grid grid-cols-2 gap-4">
                  <PanelField label="Prénom" value={form.first_name || ""} onChange={set("first_name")} required />
                  <PanelField label="Nom" value={form.last_name || ""} onChange={set("last_name")} required />
                  <PanelField label="Téléphone" value={form.phone || ""} onChange={set("phone")} required />
                  <PanelField label="Email" type="email" value={form.email || ""} onChange={set("email")} />
                  <div className="space-y-1.5">
                    <label className={labelCls}>Genre</label>
                    <select value={form.gender || ""} onChange={set("gender")} className={selectCls}>
                      <option value="">—</option>
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                    </select>
                  </div>
                  <PanelField
                    label="Date de naissance"
                    value={form.birth_date || ""}
                    onChange={set("birth_date")}
                    placeholder="JJ/MM/AAAA"
                  />
                  <PanelField label="Lieu de naissance" value={form.birth_place || ""} onChange={set("birth_place")} />
                  <div className="space-y-1.5">
                    <label className={labelCls}>Situation</label>
                    <select value={form.civil_status || ""} onChange={set("civil_status")} className={selectCls}>
                      <option value="">—</option>
                      <option value="celibataire">Célibataire</option>
                      <option value="marie">Marié(e)</option>
                      <option value="divorce">Divorcé(e)</option>
                      <option value="veuf">Veuf / Veuve</option>
                    </select>
                  </div>
                  <PanelField label="Profession" value={form.profession || ""} onChange={set("profession")} />
                </div>
              </FormSection>

              <FormSection title="Adresse">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <PanelField label="Adresse" value={form.address || ""} onChange={set("address")} />
                  </div>
                  <PanelField label="Code postal" value={form.address_postal_code || ""} onChange={set("address_postal_code")} />
                  <PanelField label="Pays" value={form.country || ""} onChange={set("country")} />
                </div>
              </FormSection>

              <FormSection title="Adhésion">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className={labelCls}>Type</label>
                    <select value={form.membership_type || ""} onChange={set("membership_type")} className={selectCls}>
                      <option value="individuel">Individuel</option>
                      <option value="famille">Famille</option>
                      <option value="etudiant">Étudiant</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelCls}>Newsletter</label>
                    <label className="flex items-center gap-2 h-[46px] px-4 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700">
                      <input type="checkbox" checked={!!form.newsletter_opt_in} onChange={set("newsletter_opt_in")} className="accent-emerald-600" />
                      Abonné
                    </label>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Année d'inscription : {member.year ?? "—"} — non modifiable.
                </p>
              </FormSection>
            </form>

            {/* Conjoint, enfants, bénéficiaire — lecture seule */}
            {(member.spouse_first_name || member.spouse_last_name) && (
              <FormSection title="Conjoint">
                <p className="text-sm text-slate-600">
                  {member.spouse_first_name} {member.spouse_last_name}
                  {member.spouse_phone ? ` · ${member.spouse_phone}` : ""}
                </p>
              </FormSection>
            )}
            <FormSection title="Enfants">
              <div className="space-y-2">
                <label className={labelCls}>Enfants à charge (-18 ans) ?</label>
                <div className="grid grid-cols-2 gap-3">
                  {[{ value: true, label: "Oui" }, { value: false, label: "Non" }].map((opt) => (
                    <label
                      key={String(opt.value)}
                      className={`flex items-center justify-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                        hasChildren === opt.value
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="has_children_under_18"
                        checked={hasChildren === opt.value}
                        onChange={() => setHasChildren(opt.value)}
                        className="accent-emerald-600"
                      />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {hasChildren && (
                <div className="mt-4 space-y-3">
                  {(form.children || []).map((child, index) => (
                    <div key={index} className="rounded-xl bg-slate-50 border border-slate-200 p-4 relative">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Enfant {index + 1}</p>
                        <button
                          type="button"
                          onClick={() => removeChild(index)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <PanelField label="Prénom" value={child.first_name} onChange={updateChild(index, "first_name")} />
                        <PanelField label="Nom" value={child.last_name} onChange={updateChild(index, "last_name")} />
                        <PanelField label="Date de naissance" type="date" value={child.birth_date || ""} onChange={updateChild(index, "birth_date")} />
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addChild}
                    className="flex items-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Ajouter un enfant
                  </button>
                </div>
              )}
            </FormSection>
            {member.beneficiary && (member.beneficiary.first_name || member.beneficiary.last_name) && (
              <FormSection title="Bénéficiaire">
                <p className="text-sm text-slate-600">
                  {member.beneficiary.first_name} {member.beneficiary.last_name}
                  {member.beneficiary.phone ? ` · ${member.beneficiary.phone}` : ""}
                </p>
              </FormSection>
            )}

            {/* Historique et enregistrement des paiements */}
            <FormSection title="Paiements">
              {payments.length === 0 ? (
                <p className="text-sm text-slate-400">Aucun paiement enregistré.</p>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden mb-4">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-400 uppercase tracking-wide">
                        <th className="text-left px-3 py-2 font-semibold">Date</th>
                        <th className="text-left px-3 py-2 font-semibold">Exercice</th>
                        <th className="text-left px-3 py-2 font-semibold">Montant</th>
                        <th className="text-left px-3 py-2 font-semibold">Mode</th>
                        <th className="text-left px-3 py-2 font-semibold">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                            {new Date(p.paid_at || p.created_at).toLocaleDateString("fr-FR")}
                          </td>
                          <td className="px-3 py-2 text-slate-500">{p.year}</td>
                          <td className="px-3 py-2 text-slate-700 font-medium whitespace-nowrap">
                            {Number(p.amount).toLocaleString("fr-FR")} €
                          </td>
                          <td className="px-3 py-2 text-slate-500 capitalize">
                            {PROVIDER_LABELS[p.provider] || p.provider}
                            {p.metadata?.reference ? ` · ${p.metadata.reference}` : ""}
                          </td>
                          <td className="px-3 py-2">
                            <StatusBadge color={p.status === "completed" ? "emerald" : p.status === "failed" ? "red" : "amber"}>
                              {p.status === "completed" ? "Complété" : p.status === "failed" ? "Échoué" : "En attente"}
                            </StatusBadge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Lien de paiement adhérent */}
              {!isSettled && (
                <div className="mb-4">
                  {linkUrl ? (
                    <CheckoutLinkResult
                      title="Lien de paiement en cours"
                      memberName={memberName}
                      url={linkUrl}
                      amount={paymentLink?.amount ?? linkPayment?.amount}
                      expiresAt={paymentLink?.expires_at ?? linkPayment?.metadata?.link_expires_at}
                      onRegenerate={() => generatePaymentLink({ regenerate: true })}
                      regenerating={linkBusy}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => generatePaymentLink()}
                      disabled={linkBusy}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold border transition-all hover:bg-slate-50 disabled:opacity-50"
                      style={{ borderColor: "#635BFF", color: "#635BFF" }}
                    >
                      {linkBusy ? "…" : "Générer un lien de paiement →"}
                    </button>
                  )}
                </div>
              )}

              {!showPaymentForm ? (
                <button
                  type="button"
                  onClick={() => {
                    const remaining = balance?.remaining_amount;
                    setPaymentForm({
                      ...EMPTY_PAYMENT_FORM,
                      year,
                      amount: remaining ? String(remaining) : "",
                    });
                    setShowPaymentForm(true);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-all"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                  Enregistrer un paiement
                </button>
              ) : (
                <form onSubmit={handleRecordPayment} className="rounded-xl border border-slate-200 p-4 space-y-4 bg-slate-50/60">
                  {balance && balance.cotisation_amount > 0 && (
                    <p className="text-xs text-slate-500">
                      Cotisation {paymentForm.year} : <strong className="text-slate-700">{Number(balance.cotisation_amount).toLocaleString("fr-FR")} €</strong>
                      {" · "}Déjà réglé : <strong className="text-slate-700">{Number(balance.paid_amount).toLocaleString("fr-FR")} €</strong>
                      {" · "}Reste dû : <strong className={balance.remaining_amount > 0 ? "text-amber-700" : "text-emerald-700"}>
                        {Number(balance.remaining_amount).toLocaleString("fr-FR")} €
                      </strong>
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <PanelField
                      label="Montant (€) *"
                      type="number"
                      value={paymentForm.amount}
                      onChange={setPayment("amount")}
                      required
                      placeholder={association?.cotisation_amount ? String(association.cotisation_amount) : "50"}
                    />
                    <PanelField
                      label="Exercice *"
                      type="number"
                      value={paymentForm.year}
                      onChange={setPayment("year")}
                      required
                    />
                    <PanelField
                      label="Date de réception"
                      type="date"
                      value={paymentForm.paid_at}
                      onChange={setPayment("paid_at")}
                    />
                    <PanelField
                      label="Référence"
                      value={paymentForm.reference}
                      onChange={setPayment("reference")}
                      placeholder="N° chèque, référence virement…"
                    />
                    <div className="col-span-2 space-y-2">
                      <label className={labelCls}>Mode de paiement *</label>
                      <div className="grid grid-cols-3 gap-3">
                        {ADMIN_RECORDABLE_PROVIDERS.map((opt) => (
                          <label
                            key={opt.value}
                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                              paymentForm.provider === opt.value
                                ? "border-emerald-500 bg-emerald-50"
                                : "border-slate-200 hover:border-slate-300 bg-white"
                            }`}
                          >
                            <input
                              type="radio"
                              name="record-payment-provider"
                              value={opt.value}
                              checked={paymentForm.provider === opt.value}
                              onChange={setPayment("provider")}
                              className="accent-emerald-600"
                            />
                            <span className="text-sm text-slate-700">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <PanelField label="Note" value={paymentForm.note} onChange={setPayment("note")} />
                    </div>
                  </div>
                  <div className="flex gap-3 justify-end pt-1">
                    <button
                      type="button"
                      onClick={() => setShowPaymentForm(false)}
                      className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs hover:bg-white transition-all"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={recordingPayment}
                      className="px-5 py-2 rounded-xl text-white text-xs font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50"
                      style={{ backgroundColor: "#059669" }}
                    >
                      {recordingPayment ? "Enregistrement…" : "Enregistrer le paiement"}
                    </button>
                  </div>
                </form>
              )}
            </FormSection>

            {/* Sinistres — décès du membre ou d'un de ses enfants */}
            <FormSection title="Sinistres">
              {activeClaims.length > 0 && (
                <div className="space-y-3 mb-4">
                  {activeClaims.map((c) => (
                    <div key={c.id} className="rounded-xl border border-slate-200 p-4 space-y-2 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{claimTargetLabel(c)}</span>
                          <StatusBadge color={c.status === "paid" ? "emerald" : "amber"}>
                            {c.status === "paid" ? "Payé" : "En attente"}
                          </StatusBadge>
                        </div>
                      </div>
                      {c.date_of_death && <p className="text-slate-600 text-xs">Décès le {c.date_of_death}</p>}
                      {c.status === "paid" ? (
                        <>
                          <p className="text-slate-700 font-medium">
                            {Number(c.paid_amount).toLocaleString("fr-FR")} € · {PROVIDER_LABELS[c.payment_method] || c.payment_method}
                            {c.paid_at ? ` · le ${new Date(c.paid_at).toLocaleDateString("fr-FR")}` : ""}
                          </p>
                          {c.payment_reference && <p className="text-xs text-slate-400">Réf. {c.payment_reference}</p>}
                        </>
                      ) : (
                        <>
                          {c.estimated_amount != null && (
                            <p className="text-slate-500 text-xs">Montant estimé : {Number(c.estimated_amount).toLocaleString("fr-FR")} €</p>
                          )}
                          {payingClaimId !== c.id ? (
                            <div className="flex gap-3 pt-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setClaimPaymentForm({
                                    ...EMPTY_CLAIM_PAYMENT_FORM,
                                    paid_amount: c.estimated_amount != null ? String(c.estimated_amount) : "",
                                  });
                                  setPayingClaimId(c.id);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-all"
                              >
                                Enregistrer le paiement
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCancelClaim(c.id)}
                                disabled={cancellingClaimId === c.id}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-red-600 transition-all disabled:opacity-50"
                              >
                                {cancellingClaimId === c.id ? "…" : "Annuler la déclaration"}
                              </button>
                            </div>
                          ) : (
                            <form onSubmit={handlePayClaim} className="rounded-xl border border-slate-200 p-4 mt-2 space-y-4 bg-slate-50/60">
                              <div className="grid grid-cols-2 gap-4">
                                <PanelField
                                  label="Montant versé (€) *"
                                  type="number"
                                  value={claimPaymentForm.paid_amount}
                                  onChange={setClaimPaymentField("paid_amount")}
                                  required
                                />
                                <PanelField
                                  label="Date de paiement"
                                  type="date"
                                  value={claimPaymentForm.paid_at}
                                  onChange={setClaimPaymentField("paid_at")}
                                />
                                <PanelField
                                  label="Référence"
                                  value={claimPaymentForm.reference}
                                  onChange={setClaimPaymentField("reference")}
                                  placeholder="N° chèque, référence virement…"
                                />
                                <div className="col-span-2 space-y-2">
                                  <label className={labelCls}>Mode de paiement *</label>
                                  <div className="grid grid-cols-3 gap-3">
                                    {ADMIN_RECORDABLE_PROVIDERS.map((opt) => (
                                      <label
                                        key={opt.value}
                                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                          claimPaymentForm.payment_method === opt.value
                                            ? "border-emerald-500 bg-emerald-50"
                                            : "border-slate-200 hover:border-slate-300 bg-white"
                                        }`}
                                      >
                                        <input
                                          type="radio"
                                          name={`claim-payment-method-${c.id}`}
                                          value={opt.value}
                                          checked={claimPaymentForm.payment_method === opt.value}
                                          onChange={setClaimPaymentField("payment_method")}
                                          className="accent-emerald-600"
                                        />
                                        <span className="text-sm text-slate-700">{opt.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                                <div className="col-span-2">
                                  <PanelField label="Note" value={claimPaymentForm.note} onChange={setClaimPaymentField("note")} />
                                </div>
                              </div>
                              <div className="flex gap-3 justify-end pt-1">
                                <button
                                  type="button"
                                  onClick={() => setPayingClaimId(null)}
                                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs hover:bg-white transition-all"
                                >
                                  Annuler
                                </button>
                                <button
                                  type="submit"
                                  disabled={payingClaim}
                                  className="px-5 py-2 rounded-xl text-white text-xs font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50"
                                  style={{ backgroundColor: "#059669" }}
                                >
                                  {payingClaim ? "Enregistrement…" : "Enregistrer le paiement"}
                                </button>
                              </div>
                            </form>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {canDeclareClaim && (
                !showClaimForm ? (
                  <button
                    type="button"
                    onClick={() => {
                      setClaimForm({
                        ...EMPTY_CLAIM_FORM,
                        target: !hasMemberClaim ? "member" : `child:0`,
                      });
                      setShowClaimForm(true);
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                    Déclarer un sinistre
                  </button>
                ) : (
                  <form onSubmit={handleDeclareClaim} className="rounded-xl border border-slate-200 p-4 space-y-4 bg-slate-50/60">
                    <div className="space-y-1.5">
                      <label className={labelCls}>Personne concernée</label>
                      <select value={claimForm.target} onChange={setClaimField("target")} className={selectCls}>
                        {!hasMemberClaim && <option value="member">Le membre</option>}
                        {availableChildren.map((c, i) => (
                          <option key={childKey(c.first_name, c.last_name)} value={`child:${i}`}>
                            {c.first_name} {c.last_name} (enfant)
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <PanelField
                        label="Date de décès"
                        value={claimForm.date_of_death}
                        onChange={setClaimField("date_of_death")}
                        placeholder="JJ/MM/AAAA"
                      />
                      <PanelField
                        label="Montant estimé (€)"
                        type="number"
                        value={claimForm.estimated_amount}
                        onChange={setClaimField("estimated_amount")}
                        placeholder={association?.avg_repatriation_cost ? String(association.avg_repatriation_cost) : "3500"}
                      />
                      <div className="col-span-2">
                        <PanelField label="Note" value={claimForm.note} onChange={setClaimField("note")} />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400">
                      {claimForm.target === "member"
                        ? "Déclarer un sinistre pour le membre le marque comme décédé : il sort du calcul de risque et de la projection du fonds."
                        : "Un sinistre enfant n'affecte pas le statut du membre."}
                    </p>
                    <div className="flex gap-3 justify-end pt-1">
                      <button
                        type="button"
                        onClick={() => setShowClaimForm(false)}
                        className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs hover:bg-white transition-all"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        disabled={declaringClaim}
                        className="px-5 py-2 rounded-xl text-white text-xs font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50"
                        style={{ backgroundColor: "#111827" }}
                      >
                        {declaringClaim ? "Déclaration…" : "Déclarer le sinistre"}
                      </button>
                    </div>
                  </form>
                )
              )}

              {!canDeclareClaim && (
                <p className="text-sm text-slate-400">Aucune personne restante à déclarer.</p>
              )}
            </FormSection>
          </div>
        )}

        {!loading && form && (
          <div className="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end bg-white">
            <button type="button" onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-all">
              Fermer
            </button>
            <button type="submit" form="member-edit-form" disabled={saving}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50"
              style={{ backgroundColor: "#059669" }}>
              {saving ? "Enregistrement…" : "Enregistrer les modifications"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberPanel;
